import { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Camera, Plus, X, ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { useTranslation } from "@/hooks/useTranslation";

interface UploadedPhoto {
  id: string;
  file?: File;
  previewUrl: string;
}

interface CleanerPhotoUploadGalleryProps {
  type: 'before' | 'after';
  photos: UploadedPhoto[];
  onAddPhotos: (files: File[]) => void;
  onRemovePhoto: (id: string) => void;
  required?: boolean;
  disabled?: boolean;
}

const CleanerPhotoUploadGallery = ({
  type,
  photos,
  onAddPhotos,
  onRemovePhoto,
  required = false,
  disabled = false,
}: CleanerPhotoUploadGalleryProps) => {
  const { tDynamic } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [viewingIndex, setViewingIndex] = useState<number | null>(null);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      onAddPhotos(Array.from(files));
      e.target.value = "";
    }
  };

  const hasPhotos = photos.length > 0;
  const isSatisfied = !required || hasPhotos;
  const label = type === 'before' ? tDynamic("BEFORE") : tDynamic("AFTER");

  return (
    <>
      <input
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        ref={fileInputRef}
        onChange={handleFileSelect}
        disabled={disabled}
      />

      <div className={cn(
        "rounded-lg border-2 border-dashed p-2.5 sm:p-3.5 transition-colors duration-150",
        !isSatisfied ? "border-destructive/50 bg-destructive/5" : 
        hasPhotos ? "border-[color:var(--pv-teal,#22c55e)]/45 bg-[color:var(--pv-teal-soft,rgb(220_252_231))]" : "border-[color:var(--pv-line,hsl(var(--muted)))]"
      )}>
        {hasPhotos ? (
          <div className="space-y-3">
            {/* Photo thumbnails - horizontal scroll on mobile, wrap on larger screens */}
            <div className="flex gap-2 sm:gap-3 overflow-x-auto sm:overflow-x-visible sm:flex-wrap pb-2 sm:pb-0 -mx-1 px-1 scrollbar-thin snap-x snap-mandatory">
              {photos.map((photo, index) => (
                <div key={photo.id} className="relative shrink-0 snap-start">
                  <button
                    type="button"
                    onClick={() => setViewingIndex(index)}
                    className={cn(
                      "aspect-square w-[4.25rem] overflow-hidden rounded-lg border border-[color:var(--pv-line,hsl(var(--border)))] bg-muted sm:w-[4.5rem] md:w-20",
                      "transition-transform duration-150 active:scale-95"
                    )}
                  >
                    <img
                      src={photo.previewUrl}
                      alt={`${label} ${index + 1}`}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </button>
                  {!disabled && (
                    <button
                      type="button"
                      onClick={() => onRemovePhoto(photo.id)}
                      className="absolute top-1 right-1 w-3.5 h-3.5 sm:w-3.5 sm:h-3.5 rounded-full bg-destructive text-destructive-foreground flex items-center justify-center shadow active:scale-90 touch-manipulation"
                      aria-label={`Remove photo ${index + 1}`}
                    >
                      <X className="w-2 h-2 sm:w-2 sm:h-2" />
                    </button>
                  )}
                </div>
              ))}
              
              {/* Add more button - inline with photos */}
              {!disabled && (
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className={cn(
                    "flex aspect-square w-[4.25rem] shrink-0 snap-start items-center justify-center rounded-lg border-2 border-dashed border-[color:var(--pv-line,hsl(var(--muted-foreground)))]/40 transition-colors duration-150 sm:w-[4.5rem] md:w-20",
                    "hover:border-[color:var(--pv-teal,hsl(var(--primary)))] hover:bg-muted/50",
                    "active:scale-95 active:bg-muted/60"
                  )}
                >
                  <Plus className="w-6 h-6 sm:w-5 sm:h-5 text-muted-foreground" />
                </button>
              )}
            </div>
            
            <p className={cn(
              "text-xs sm:text-sm font-medium",
              isSatisfied ? "text-[color:var(--pv-teal-hover,#15803d)]" : "text-destructive"
            )}>
              {photos.length} {label} {tDynamic("photo")}{photos.length !== 1 ? 's' : ''} ✓
            </p>
          </div>
        ) : (
          /* Empty state - full width tap target on mobile */
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={disabled}
            className={cn(
              "w-full flex items-center justify-center gap-2 min-h-[56px] sm:min-h-[48px] py-3 rounded-md",
              "active:bg-muted/50 transition-colors",
              disabled && "opacity-50 cursor-not-allowed"
            )}
          >
            <Camera className="w-5 h-5 sm:w-4 sm:h-4 shrink-0" />
            <span className={cn(
              "text-sm sm:text-base font-medium",
              !isSatisfied ? "text-destructive" : "text-muted-foreground"
            )}>
              {required 
                ? `${tDynamic("Add")} ${label} ${tDynamic("photo")} (${tDynamic("required")})`
                : `${tDynamic("Add")} ${label} ${tDynamic("photo")}`
              }
            </span>
          </button>
        )}
      </div>

      {/* Fullscreen Lightbox viewer */}
      <Dialog open={viewingIndex !== null} onOpenChange={() => setViewingIndex(null)}>
        <DialogContent className="max-w-[100vw] w-screen h-[100dvh] max-h-[100dvh] p-0 bg-black border-none rounded-none sm:rounded-lg sm:max-w-4xl sm:h-auto sm:max-h-[90vh]" aria-describedby="photo-gallery-desc">
          <DialogTitle className="sr-only">Photo</DialogTitle>
          <DialogDescription id="photo-gallery-desc" className="sr-only">Viewing photo {viewingIndex !== null ? viewingIndex + 1 : 0} of {photos.length}.</DialogDescription>
          {viewingIndex !== null && (
            <PhotoViewer
              photos={photos}
              currentIndex={viewingIndex}
              onIndexChange={setViewingIndex}
              onClose={() => setViewingIndex(null)}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

// Internal photo viewer component with mobile swipe support
const PhotoViewer = ({
  photos,
  currentIndex,
  onIndexChange,
  onClose,
}: {
  photos: UploadedPhoto[];
  currentIndex: number;
  onIndexChange: (index: number) => void;
  onClose: () => void;
}) => {
  const goNext = () => onIndexChange((currentIndex + 1) % photos.length);
  const goPrev = () => onIndexChange((currentIndex - 1 + photos.length) % photos.length);

  const handleTouchStart = (e: React.TouchEvent) => {
    (e.currentTarget as any)._touchStartX = e.touches[0].clientX;
    (e.currentTarget as any)._touchStartY = e.touches[0].clientY;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    const startX = (e.currentTarget as any)._touchStartX;
    const startY = (e.currentTarget as any)._touchStartY;
    const diffX = e.changedTouches[0].clientX - startX;
    const diffY = e.changedTouches[0].clientY - startY;
    
    // Only swipe if horizontal movement is greater than vertical
    if (Math.abs(diffX) > Math.abs(diffY) && Math.abs(diffX) > 50) {
      diffX > 0 ? goPrev() : goNext();
    }
  };

  return (
    <div 
      className="relative w-full h-full min-h-[60vh] sm:min-h-[50vh] flex items-center justify-center touch-pan-y"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {/* Close button - larger on mobile */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 z-10 w-12 h-12 sm:w-10 sm:h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center active:bg-white/30"
        aria-label="Close"
      >
        <X className="w-6 h-6 sm:w-5 sm:h-5 text-white" />
      </button>

      {/* Navigation arrows - hidden on mobile */}
      {photos.length > 1 && (
        <>
          <button
            onClick={goPrev}
            className="hidden sm:flex absolute left-2 z-10 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 items-center justify-center"
          >
            <ChevronLeft className="w-6 h-6 text-white" />
          </button>
          <button
            onClick={goNext}
            className="hidden sm:flex absolute right-2 z-10 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 items-center justify-center"
          >
            <ChevronRight className="w-6 h-6 text-white" />
          </button>
        </>
      )}

      <img
        src={photos[currentIndex].previewUrl}
        alt={`Photo ${currentIndex + 1}`}
        className="max-w-full max-h-[75vh] sm:max-h-[80vh] object-contain px-2"
      />

      {/* Dots indicator - larger on mobile */}
      {photos.length > 1 && (
        <div className="absolute bottom-16 sm:bottom-4 left-1/2 -translate-x-1/2 flex gap-2 sm:gap-1.5 p-2 rounded-full bg-black/30">
          {photos.map((_, index) => (
            <button
              key={index}
              onClick={() => onIndexChange(index)}
              className={cn(
                "rounded-full transition-all",
                "w-3 h-3 sm:w-2 sm:h-2",
                index === currentIndex ? "bg-white scale-110" : "bg-white/40"
              )}
              aria-label={`Go to photo ${index + 1}`}
            />
          ))}
        </div>
      )}

      {/* Counter */}
      <div className="absolute top-4 left-4 text-white/80 text-sm font-medium px-3 py-1 rounded-full bg-black/30">
        {currentIndex + 1} / {photos.length}
      </div>

      {/* Swipe hint on mobile */}
      {photos.length > 1 && (
        <div className="sm:hidden absolute bottom-6 left-1/2 -translate-x-1/2 text-white/60 text-xs">
          Swipe to navigate
        </div>
      )}
    </div>
  );
};

export default CleanerPhotoUploadGallery;
